
import React, { useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { City } from '../../../data/types/city-types';
import { Region } from '../../../data/types/region-types';
import { useCityMarkers } from '../../../hooks/use-city-markers';

interface CityMarkersProps {
  map: mapboxgl.Map;
  cities: City[];
  onCityClick: (city: City) => void;
  markerRefs: mapboxgl.Marker[];
  selectedCity: City | null | undefined;
  selectedRegion: Region | null | undefined;
  showAllMexicoCities?: boolean;
  showFunding?: boolean;
}

const CityMarkers: React.FC<CityMarkersProps> = ({ 
  map, 
  cities, 
  onCityClick,
  markerRefs, 
  selectedCity,
  selectedRegion,
  showAllMexicoCities = false,
  showFunding = true
}) => {
  // Use the custom hook to manage city markers
  const markers = useCityMarkers(map, cities, selectedCity, onCityClick, showFunding);
  
  // Update marker references for cleanup
  useEffect(() => {
    if (markers.length > 0) {
      markerRefs.length = 0;
      markers.forEach(marker => {
        markerRefs.push(marker);
      });
    }
    
    return () => {
      markers.forEach(marker => marker.remove());
    };
  }, [markers, markerRefs]);

  // Add data attributes to map container for styling
  useEffect(() => {
    if (map) {
      const container = map.getContainer();
      
      // Set an attribute on zoom change to help with CSS targeting
      const handleZoom = () => {
        const zoom = map.getZoom();
        let zoomLevel = 'country';
        
        if (zoom >= 8) {
          zoomLevel = 'city';
        } else if (zoom >= 5) {
          zoomLevel = 'region';
        }
        
        container.setAttribute('data-zoom-level', zoomLevel);
      };
      
      // Run once on mount to set initial zoom level
      handleZoom();
      
      // Listen for zoom events
      map.on('zoom', handleZoom);
      
      if (selectedRegion) {
        container.setAttribute('data-region', selectedRegion.region_id);
      }
      
      // Add showFunding data attribute
      container.setAttribute('data-show-funding', showFunding ? 'true' : 'false');
      
      return () => {
        map.off('zoom', handleZoom);
        container.removeAttribute('data-region');
        container.removeAttribute('data-zoom-level');
        container.removeAttribute('data-show-funding');
      };
    }
  }, [selectedRegion, map, showFunding]);

  return null;
};

export default CityMarkers;
